import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { Card, CardContent } from "../../../components/ui/common/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "../../../components/ui/common/collapsible";

interface ProcessingStep {
  name: string;
  impact: number;
}

interface ProcessingImpactsCardProps {
  processingSteps: ProcessingStep[];
}

/**
 * ProcessingImpactsCard Component
 * Single Responsibility: Display processing impacts as bar chart
 */
export const ProcessingImpactsCard: React.FC<ProcessingImpactsCardProps> = ({
  processingSteps
}) => {
  const [processOpen, setProcessOpen] = useState(true);

  // Calculate max impact for scaling bars
  const maxProcessImpact = Math.max(...processingSteps.map(step => step.impact), 1);

  return (
    <Collapsible open={processOpen} onOpenChange={setProcessOpen}>
      <Card className="tw-border-border tw-py-0">
        <CollapsibleTrigger asChild>
          <button className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-5 tw-text-left hover:tw-bg-muted/30 tw-transition-colors tw-rounded-t-lg">
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-foreground">Processing Impacts</p>
              <p className="tw-text-xs tw-text-muted-foreground tw-mt-0.5">
                Impact kgCO₂ per unit across processing steps
              </p>
            </div>
            <ChevronDown
              className={`tw-h-4 tw-w-4 tw-text-muted-foreground tw-transition-transform tw-duration-200 ${
                processOpen ? "tw-rotate-180" : ""
              }`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="tw-px-5 tw-pb-5 tw-pt-0">
            {processingSteps.length === 0 ? (
              <div className="tw-text-center tw-py-1 tw-text-muted-foreground tw-text-sm">
                No processing steps data available
              </div>
            ) : (
              <div className="tw-overflow-x-auto">
                <div className="tw-flex tw-gap-4 tw-min-w-max tw-pb-2">
                  {processingSteps.map((step, index) => {
                    const barHeight = Math.max(12, (step.impact / maxProcessImpact) * 100);
                    return (
                      <div
                        key={index}
                        className="tw-flex tw-flex-col tw-items-center tw-group tw-w-20 tw-shrink-0 hover:tw-bg-muted/30 tw-rounded-md tw-p-1 tw-transition-colors"
                      >
                        <div className="tw-h-24 tw-w-full tw-flex tw-items-end tw-justify-center tw-mb-2">
                          <div
                            className="tw-w-8 tw-rounded-t-md tw-bg-primary/70 group-hover:tw-bg-primary tw-transition-all tw-duration-300"
                            style={{ height: `${barHeight}%` }}
                          />
                        </div>
                        <p className="tw-text-[10px] tw-text-muted-foreground tw-text-center tw-leading-tight tw-font-medium">
                          {step.name}
                        </p>
                        <p className="tw-text-[10px] tw-font-mono tw-text-foreground tw-mt-0.5">
                          {step.impact.toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
